import React, { useEffect, useState } from "react";
import toast, { Toaster } from 'react-hot-toast';
import { Link } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Stack,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FaSearch } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { fetchPayoutDetails } from "../../Redux/Reducers/PayoutReducer";
import PageHeader1 from "../../components/common/PageHeader1";

const PayoutDetails = () => {
  const dispatch = useDispatch();

  // --- REDUX STATE ---
  const payoutDataList = useSelector((state) => state?.PayoutReducer?.payoutDetails || []);
  const { status, error, paginationDetail = {} } = useSelector((state) => state.PayoutReducer);

  // --- COMPONENT STATE ---
  // State for the main page filter
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // State for dialogs and loading indicators
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // --- DATA FETCHING & EFFECTS ---
  // Load data on mount and when pagination changes (only if search is not applied)
  useEffect(() => {
    // If search is applied, don't trigger this effect for pagination
    if (appliedSearch) {
      return;
    }

    const page = paginationModel.page + 1;
    const limit = paginationModel.pageSize;
    dispatch(fetchPayoutDetails({
      page,
      limit,
      startDate: appliedStartDate,
      endDate: appliedEndDate
    }));
  }, [dispatch, paginationModel, appliedStartDate, appliedEndDate, appliedSearch]);

  // Show toast for fetch errors
  useEffect(() => {
    if (status === 'failed' && error) {
      toast.error(`Error: ${error}`);
    }
  }, [status, error]);

  // --- EVENT HANDLERS ---
  const handleSearch = () => {
    if (!searchValue.trim()) {
      // If search is empty, reset and load all data
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      dispatch(fetchPayoutDetails({
        page: 1,
        limit: 10,
        startDate: appliedStartDate,
        endDate: appliedEndDate
      }));
      return;
    }
    setAppliedSearch(searchValue);
    // Call API with only search parameter (no page/limit)
    dispatch(fetchPayoutDetails({ search: searchValue.trim() }));
  };

  // Handle when search input is cleared
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    // If search is cleared (empty), reset and load all data
    if (!value.trim() && appliedSearch) {
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      dispatch(fetchPayoutDetails({
        page: 1,
        limit: 10,
        startDate: appliedStartDate,
        endDate: appliedEndDate
      }));
    }
  };

  // Filter the data grid by dispatching an action with date params
  const handleApplyFilter = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both a Start and End Date to filter.");
      return;
    }
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedSearch(""); // Clear search when applying date filter
    setPaginationModel({ page: 0, pageSize: 10 }); // Reset to first page when applying filter
    dispatch(fetchPayoutDetails({
      page: 1,
      limit: 10,
      startDate: startDate,
      endDate: endDate
    }));
  };

  // Reset filters and fetch all data
  const handleReset = () => {
    setSearchValue("");
    setStartDate("");
    setEndDate("");
    setAppliedSearch("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setPaginationModel({ page: 0, pageSize: 10 });
    dispatch(fetchPayoutDetails({ page: 1, limit: 10 }));
    toast.success("Filters reset. Showing all data.");
  };

  // --- DATA & COLUMNS for DataGrid ---
  // Calculate rowCount: use paginationDetail if available, otherwise use data length (for search)
  // Backward compatibility: check multiple possible fields
  const rowCount = appliedSearch
    ? (payoutDataList?.length || 0)
    : (paginationDetail?.totalRecords || paginationDetail?.totalDocs || payoutDataList?.length || 0);

  // The 'rows' variable is ALWAYS derived from the Redux state, so it reflects the latest filtered data.
  const rows = payoutDataList.map((item, index) => ({
    ...item,
    id: item.astrologerId || index,
    sono: (paginationModel.page * paginationModel.pageSize) + index + 1,
    adjustBalance: item?.adjustBalance || 0,
    createdAt: item?.createdAt ? new Date(item.createdAt).toLocaleString() : "",
    updatedAt: item?.updatedAt ? new Date(item.updatedAt).toLocaleString() : "",
  }));

  const columns = [
    { field: "sono", headerName: "S.No.", width: 80 },
    { field: "astrologerId", headerName: "Astrologer ID", width: 150 },
    { field: "fullName", headerName: "Astrologer Name", width: 180 },
    { field: "earningAmount", headerName: "Astro Total Amount", width: 160, type: "number" },
    { field: "tdsAmount", headerName: "TDS Amount", width: 130, type: "number" },
    { field: "amountAfterTds", headerName: "Astro Amount After TDS", width: 190, type: "number" },
    { field: "adminProfit", headerName: "Admin Total Profit", width: 170, type: "number" },
    { field: "disbursedAmount", headerName: "Disbursed Amount", width: 160, type: "number" },
    { field: "adjustBalance", headerName: "Adjustment", width: 130, type: "number" },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      renderCell: ({ row }) => (
        <div>
          <IconButton component={Link} to={`/payout-reportsView/${row.astrologerId}`}>
            <i className="icofont-eye text-success"></i>
          </IconButton>
        </div>
      ),
    },
  ];

  /**
   * NEW EXPORT LOGIC:
   * Exports the currently visible 'rows' from the DataGrid to a CSV file.
   * This is a client-side operation and does not make a new API call.
   */
  const handleExportSubmit = () => {
    setIsExporting(true);

    // Helper to properly format data for CSV (handles commas, quotes)
    const escapeCsvCell = (cell) => {
      if (cell === null || cell === undefined) return '';
      const cellString = String(cell);
      if (cellString.includes(',') || cellString.includes('"') || cellString.includes('\n')) {
        return `"${cellString.replace(/"/g, '""')}"`;
      }
      return cellString;
    };

    try {
      // 1. Get the columns to export (we'll skip the "Actions" column)
      const exportableColumns = [...columns, { field: 'transactionId', headerName: 'Transaction no', width: 180 }, { field: 'paymentMethod', headerName: 'NEFT/RTGS/IMPS', width: 150 }, { field: 'Transaction Date', headerName: 'Transaction Date', width: 180 }].filter(col => col.field !== 'actions');

      // 2. Create the header row from column definitions
      const headerRow = exportableColumns.map(col => escapeCsvCell(col.headerName)).join(',');

      // 3. Create data rows from the 'rows' state variable
      const dataRows = rows.map(row =>
        exportableColumns.map(col =>
          escapeCsvCell(row[col.field])
        ).join(',')
      );

      // 4. Combine header and data into a single CSV string
      const csvContent = [headerRow, ...dataRows].join('\n');

      // 5. Create a Blob and trigger the browser download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      // Create a dynamic filename, including filter dates if they exist
      const fileName = (appliedStartDate && appliedEndDate)
        ? `payout-details-${appliedStartDate}-to-${appliedEndDate}.csv`
        : 'payout-details.csv';
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Export successful! Check your downloads.');
      setExportDialogOpen(false); // Close the confirmation dialog
    } catch (error) {
      toast.error(`Export failed: ${error.message}`);
      console.error("Client-side export error:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Import logic remains unchanged
  const handleImportSubmit = async () => {
    // ... (import logic is the same as before)
    if (!importFile) {
      toast.error("Please select an Excel file to import.");
      return;
    }
    const token = localStorage.getItem('vedhvidhi-admin-token');
    if (!token) {
      toast.error("Authentication token not found. Please log in again.");
      return;
    }
    setIsImporting(true);
    const baseUrl = process.env.REACT_APP_BASEURL;
    const url = `${baseUrl}/admin/uploadPayoutExcel`;
    const formData = new FormData();
    formData.append('file', importFile);

    const importPromise = fetch(url, { method: 'POST', headers: { 'Authorization': token }, body: formData })
      .then(async (response) => {
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'File upload failed.');
        return result;
      });

    toast.promise(importPromise, {
      loading: 'Uploading and processing file...',
      success: 'Import successful! Refreshing data...',
      error: (err) => `Import failed: ${err.message}`,
    });

    try {
      await importPromise;
      dispatch(fetchPayoutDetails()); // Refresh grid data
      setImportDialogOpen(false);
      setImportFile(null);
    } catch (error) {
      console.error("Import error:", error);
    } finally {
      setIsImporting(false);
    }
  };


  return (
    <div className="body d-flex py-1">
      <div className="container-xxl">
        <Toaster />
        <PageHeader1 pagetitle="Payout Details" />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <Box sx={{ width: "100%" }}>
                  {/* Search and Date Filter Section - Inside table card, single line */}
                  <Box display="flex" alignItems="flex-end" gap={2} mb={2} flexWrap="wrap">
                    <TextField
                      placeholder="Search..."
                      value={searchValue}
                      onChange={handleSearchChange}
                      size="small"
                      sx={{ width: '250px' }}
                    />
                    <IconButton
                      onClick={handleSearch}
                      disabled={status === "loading" || !searchValue.trim()}
                      sx={{
                        height: '40px',
                        width: '40px',
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '&:disabled': {
                          backgroundColor: 'action.disabledBackground',
                          color: 'action.disabled',
                        }
                      }}
                    >
                      <FaSearch />
                    </IconButton>
                    <TextField
                      type="date"
                      label="Start Date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: '180px' }}
                    />
                    <TextField
                      type="date"
                      label="End Date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: '180px' }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleApplyFilter}
                      disabled={status === "loading"}
                      sx={{ height: '40px' }}
                    >
                      {status === "loading" ? "Loading..." : "Apply"}
                    </Button>
                    <Button variant="contained" color="success" onClick={() => setExportDialogOpen(true)} sx={{ height: '40px' }}>
                      Export
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={() => setImportDialogOpen(true)} sx={{ height: '40px' }}>
                      Import
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleReset}
                      sx={{ height: '40px' }}
                    >
                      Clear
                    </Button>
                  </Box>

                  <Box sx={{ height: 600, width: "100%" }}>
                    <DataGrid
                      rows={rows}
                      columns={columns}
                      loading={status === 'loading'}
                      rowCount={rowCount}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationMode={appliedSearch ? "client" : "server"}
                      disableRowSelectionOnClick
                      getRowId={(row) => row.id}
                    />
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- DIALOGS --- */}
      {/* SIMPLIFIED Export Dialog - just a confirmation */}
      <Dialog open={exportDialogOpen} onClose={() => !isExporting && setExportDialogOpen(false)}>
        <DialogTitle>Export Payout Details</DialogTitle>
        <DialogContent>
          <Typography>
            This will export the currently displayed data (including any active filters) as a CSV file.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={isExporting}>Cancel</Button>
          <Button onClick={handleExportSubmit} variant="contained" disabled={isExporting}>
            {isExporting ? "Exporting..." : "Confirm & Export"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog is unchanged */}
      <Dialog open={importDialogOpen} onClose={() => !isImporting && setImportDialogOpen(false)}>
        <DialogTitle>Import Payout Excel</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>Select an Excel file (.xlsx, .xls) to upload.</Typography>
          <Button variant="contained" component="label">
            Choose File
            <input type="file" hidden accept=".xlsx, .xls" onChange={(e) => setImportFile(e.target.files[0])} />
          </Button>
          {importFile && <Typography sx={{ mt: 1, ml: 1, display: 'inline' }}>{importFile.name}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={isImporting}>Cancel</Button>
          <Button onClick={handleImportSubmit} variant="contained" disabled={!importFile || isImporting}>
            {isImporting ? "Importing..." : "Import & Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PayoutDetails;